import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import { INavigation } from '@/types/navigation';
import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const NAVIGATION_LIST = 'theme-changes-current-theme' as const;
const NAVIGATION_DETAIL = 'navigation-detail' as const;

const isPartner = Cookies.get('_partner_id') !== undefined;
export const listNavigation = queryKeysFactory(NAVIGATION_LIST);
export const detailNavigation = queryKeysFactory(NAVIGATION_DETAIL);

export const useGetNavigations = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminMenuListResponse,
      FetchError,
      HttpTypes.AdminMenuListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: listNavigation.list(query),
    queryFn: async () =>
      isPartner ? sdk.partner.navigation.list(query) : sdk.admin.navigation.list(query),
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetNavigationsInfinite = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.AdminMenuListResponse,
      FetchError,
      HttpTypes.AdminMenuListResponse,
      HttpTypes.AdminMenuListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: listNavigation.list(query),
    queryFn: async ({ pageParam = 1 }) =>
      isPartner
        ? sdk.partner.navigation.list({ ...query, page: pageParam as number })
        : sdk.admin.navigation.list({ ...query, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { data, totalCount } = lastPage;
      const currentPage = allPages.length;
      const pageSize = data.length;

      if (currentPage * pageSize < Number(totalCount)) {
        return currentPage + 1;
      }
      return undefined;
    },
    ...options,
  });
};

export const useGetNavigation = (
  id?: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<INavigation>,
      FetchError,
      HttpTypes.IResponse<INavigation>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: detailNavigation.detail(id as string),
    queryFn: async () =>
      isPartner
        ? sdk.partner.navigation.retrieve(id as string)
        : sdk.admin.navigation.retrieve(id as string),
    ...options,
  });

  return { ...data, ...rest };
};

export const useCreateNavigation = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminNavigationCreateResponse>,
    FetchError,
    HttpTypes.AdminNavigationCreatePayload
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.navigation.create(payload) : sdk.admin.navigation.create(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteNavigation = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminNavigationDeleteResponse>,
    FetchError,
    string
  >
) => {
  return useMutation({
    mutationFn: (id) =>
      isPartner ? sdk.partner.navigation.delete(id) : sdk.admin.navigation.delete(id),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};
